/**
 * Browser-side image pyramid. The server never has to decode a huge file:
 * we produce WebP variants here and (in Phase 2) upload them next to the original.
 */

export const VARIANT_SIZES = { thumb: 256, medium: 1024, large: 2560 } as const;
export type VariantName = keyof typeof VARIANT_SIZES | 'original';
export const VARIANT_ORDER: VariantName[] = ['thumb', 'medium', 'large', 'original'];

export interface VariantBlob {
  blob: Blob;
  w: number;
  h: number;
}

export interface GeneratedVariants {
  width: number;
  height: number;
  variants: Partial<Record<Exclude<VariantName, 'original'>, VariantBlob>>;
  /** Re-encoded original (lossy WebP, possibly downscaled) when compression produced something worth keeping. */
  original?: VariantBlob;
}

export interface CompressOptions {
  /** Re-encode the original as lossy WebP. */
  webp: boolean;
  /** 0..1 */
  quality: number;
  /** Longest side to keep, in pixels; 0 = no limit. */
  maxSide: number;
}

/** Smallest variant whose longest side covers `neededPx`, else the original. */
export function pickVariant(neededPx: number): VariantName {
  if (neededPx <= VARIANT_SIZES.thumb) return 'thumb';
  if (neededPx <= VARIANT_SIZES.medium) return 'medium';
  if (neededPx <= VARIANT_SIZES.large) return 'large';
  return 'original';
}

export async function generateVariants(file: Blob, compress?: CompressOptions): Promise<GeneratedVariants> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    let width = bitmap.width;
    let height = bitmap.height;
    let source: ImageBitmap | HTMLCanvasElement = bitmap;
    const out: GeneratedVariants = { width, height, variants: {} };

    // Optionally replace the stored original: lossy WebP at full size, or downscaled to the cap.
    // Animated GIFs keep their animation only as the original file, so they are left alone.
    if (compress?.webp && file.type !== 'image/gif') {
      const cap = compress.maxSide > 0 ? compress.maxSide : Infinity;
      const longest0 = Math.max(width, height);
      const capped = longest0 > cap;
      if (capped) {
        const s = cap / longest0;
        width = Math.max(1, Math.round(bitmap.width * s));
        height = Math.max(1, Math.round(bitmap.height * s));
        source = downscale(bitmap, width, height);
      }
      const canvas = source instanceof HTMLCanvasElement ? source : draw(source, width, height);
      const blob = await canvasToBlob(canvas, 'image/webp', compress.quality);
      // A browser without a WebP encoder (Safari) hands back a PNG; a WebP that is not smaller
      // than an already-compressed original is not worth it either, unless it was downscaled.
      if (blob.type === 'image/webp' && (capped || blob.size < file.size)) {
        out.original = { blob, w: width, h: height };
        out.width = width;
        out.height = height;
        source = canvas;
      } else {
        width = bitmap.width;
        height = bitmap.height;
        source = bitmap;
      }
    }
    const longest = Math.max(width, height);

    // Generate from largest to smallest, each step downscaling the previous result for quality.
    for (const name of ['large', 'medium', 'thumb'] as const) {
      const max = VARIANT_SIZES[name];
      if (longest <= max) continue; // the original already is small enough for this level
      const scale = max / longest;
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = downscale(source, w, h);
      const blob = await canvasToBlob(canvas, 'image/webp', 0.86);
      out.variants[name] = { blob, w, h };
      source = canvas;
    }
    return out;
  } finally {
    bitmap.close();
  }
}

/**
 * WebP pyramid of a canvas that already holds the final pixels (a painted sketch-sheet layer).
 * The caller keeps the canvas's own lossless encoding as the original.
 */
export async function variantsFromCanvas(canvas: HTMLCanvasElement): Promise<GeneratedVariants> {
  const width = canvas.width;
  const height = canvas.height;
  const out: GeneratedVariants = { width, height, variants: {} };
  const longest = Math.max(width, height);
  let source: HTMLCanvasElement = canvas;
  for (const name of ['large', 'medium', 'thumb'] as const) {
    const max = VARIANT_SIZES[name];
    if (longest <= max) continue;
    const scale = max / longest;
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const c = downscale(source, w, h);
    const blob = await canvasToBlob(c, 'image/webp', 0.86);
    out.variants[name] = { blob, w, h };
    source = c;
  }
  return out;
}

/** Stepwise halving before the final resample avoids aliasing on big downscales. */
function downscale(source: ImageBitmap | HTMLCanvasElement, w: number, h: number): HTMLCanvasElement {
  let cur: ImageBitmap | HTMLCanvasElement = source;
  let cw = source.width;
  let ch = source.height;
  while (cw / 2 >= w && ch / 2 >= h) {
    cw = Math.floor(cw / 2);
    ch = Math.floor(ch / 2);
    cur = draw(cur, cw, ch);
  }
  return cur.width === w && cur.height === h && cur instanceof HTMLCanvasElement ? cur : draw(cur, w, h);
}

function draw(source: ImageBitmap | HTMLCanvasElement, w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type, quality);
  });
}
